import { lazy, Suspense, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute.jsx";
import InstallPrompt from "./components/InstallPrompt.jsx";
import Splash from "./components/Splash.jsx";
import Gate from "./components/Gate.jsx";
import { Toaster } from "./components/ui/toaster";
import { currentSeasonClass } from "./lib/season";
import { getToken } from "./lib/api";

// Code-splitting: each route becomes its own bundle so the first-paint
// payload drops from ~1.5-2 MB to ~400 KB. The Splash component covers
// the whole viewport while any lazy chunk is loading, so the user never
// sees a flash of blank UI.
const Home = lazy(() => import("./pages/Home.jsx"));
const Create = lazy(() => import("./pages/Create.jsx"));
const Reserve = lazy(() => import("./pages/Reserve.jsx"));
const Landing = lazy(() => import("./pages/Landing.jsx"));

// Evaluated on every render of "/" so it reflects a login that just happened.
const HomeOrLanding = () => (getToken() ? <ProtectedRoute><Home /></ProtectedRoute> : <Landing />);
const UserNeighbors = lazy(() => import("./pages/UserNeighbors.jsx"));
const ActivityDetail = lazy(() => import("./pages/ActivityDetail.jsx"));
const MyWall = lazy(() => import("./pages/MyWall.jsx"));
const Profile = lazy(() => import("./pages/Profile.jsx"));
const Auth = lazy(() => import("./pages/Auth.jsx"));
const GoogleCallback = lazy(() => import("./pages/GoogleCallback.jsx"));
const PublicProfile = lazy(() => import("./pages/PublicProfile.jsx"));
const BusinessManage = lazy(() => import("./pages/BusinessManage.jsx"));
const BusinessMenu = lazy(() => import("./pages/BusinessMenu.jsx"));
const Vicinato = lazy(() => import("./pages/Vicinato.jsx"));
const Messages = lazy(() => import("./pages/Messages.jsx"));
const MessageThread = lazy(() => import("./pages/MessageThread.jsx"));
const Reports = lazy(() => import("./pages/Reports.jsx"));
const AdminSettings = lazy(() => import("./pages/AdminSettings.jsx"));
const CommunityRules = lazy(() => import("./pages/CommunityRules.jsx"));
const SupportTickets = lazy(() => import("./pages/SupportTickets.jsx"));
const AdminMonitor = lazy(() => import("./pages/AdminMonitor.jsx"));
const Terms = lazy(() => import("./pages/Terms.jsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.jsx"));
const MockupPremiumLive = lazy(() => import("./pages/MockupPremiumLive.jsx"));
const AdminSimulations = lazy(() => import("./pages/AdminSimulations.jsx"));
const SimulationEditor = lazy(() => import("./pages/SimulationEditor.jsx"));
const SimulationRoot = lazy(() => import("./pages/SimulationRoot.jsx"));
const SimulationWall = lazy(() => import("./pages/SimulationWall.jsx"));

// Non-blocking fallback: an empty div. The real splash sits above it
// via <Splash /> which handles the brand entry animation.
const RouteFallback = () => <div aria-busy="true" />;

function App() {
  useEffect(() => {
    document.title = "Wall Week - La community del tuo paese";
    const cls = currentSeasonClass();
    if (cls) {
      document.body.classList.add(cls);
      return () => document.body.classList.remove(cls);
    }
  }, []);

  return (
    <div className="App">
      <Splash />
      <Gate>
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/google" element={<GoogleCallback />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/mockup-premium-live" element={<MockupPremiumLive />} />
              <Route path="/" element={<HomeOrLanding />} />
              <Route path="/muro" element={<ProtectedRoute><MyWall /></ProtectedRoute>} />
              <Route path="/crea" element={<ProtectedRoute><Create /></ProtectedRoute>} />
              <Route path="/prenota" element={<ProtectedRoute><Reserve /></ProtectedRoute>} />
              <Route path="/profilo" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/attivita/:id" element={<ProtectedRoute><ActivityDetail /></ProtectedRoute>} />
              <Route path="/utente/:id" element={<ProtectedRoute><PublicProfile /></ProtectedRoute>} />
              <Route path="/gestione-attivita" element={<ProtectedRoute><BusinessManage /></ProtectedRoute>} />
              <Route path="/menu/:userId" element={<ProtectedRoute><BusinessMenu /></ProtectedRoute>} />
              <Route path="/utente/:id/vicinato" element={<ProtectedRoute><UserNeighbors /></ProtectedRoute>} />
              <Route path="/vicinato" element={<ProtectedRoute><Vicinato /></ProtectedRoute>} />
              <Route path="/messaggi" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
              <Route path="/messaggi/:userId" element={<ProtectedRoute><MessageThread /></ProtectedRoute>} />
              <Route path="/admin/segnalazioni" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
              <Route path="/admin/impostazioni" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
              <Route path="/regolamento" element={<ProtectedRoute><CommunityRules /></ProtectedRoute>} />
              <Route path="/admin/supporto" element={<ProtectedRoute><SupportTickets /></ProtectedRoute>} />
              <Route path="/admin/monitor" element={<ProtectedRoute><AdminMonitor /></ProtectedRoute>} />
              <Route path="/admin/simulazioni" element={<ProtectedRoute><AdminSimulations /></ProtectedRoute>} />
              <Route path="/admin/simulazioni/nuova" element={<ProtectedRoute><SimulationEditor /></ProtectedRoute>} />
              <Route path="/admin/simulazioni/:id" element={<ProtectedRoute><SimulationEditor /></ProtectedRoute>} />
              <Route path="/simulazione/:token" element={<SimulationRoot />}>
                <Route index element={<PublicProfile />} />
                <Route path="menu" element={<BusinessMenu />} />
                <Route path="post/:id" element={<ActivityDetail />} />
                <Route path="bacheca" element={<SimulationWall />} />
              </Route>
            </Routes>
          </Suspense>
          <InstallPrompt />
        </BrowserRouter>
      </Gate>
      <Toaster />
    </div>
  );
}

export default App;
